import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import theme from '../../theme';
import { JobCard } from '../../types';

interface MakeModelBreakdownProps {
  jobCards: JobCard[];
}

export const MakeModelBreakdown: React.FC<MakeModelBreakdownProps> = ({ jobCards }) => {
  const breakdown = useMemo(() => {
    // Only count active job cards (in workshop now)
    const activeCards = jobCards.filter(jc => 
      jc.jobStatus === 'pending' || jc.jobStatus === 'in_progress'
    );

    // Group by make/model
    const grouped = activeCards.reduce((acc, jc) => {
      const makeModel = jc.makeModel || 'Unknown';
      if (!acc[makeModel]) {
        acc[makeModel] = {
          count: 0,
          vehicles: [],
        };
      }
      acc[makeModel].count++;
      acc[makeModel].vehicles.push(jc.licensePlate);
      return acc;
    }, {} as Record<string, { count: number; vehicles: string[] }>);

    // Convert to array and sort by count
    return Object.entries(grouped)
      .map(([makeModel, data]) => ({
        makeModel,
        count: data.count,
        vehicles: data.vehicles,
      }))
      .sort((a, b) => b.count - a.count);
  }, [jobCards]);

  const total = breakdown.reduce((sum, item) => sum + item.count, 0);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Workshop Breakdown by Vehicle Model</Text>
      <Text style={styles.subtitle}>{total} vehicles currently in workshop</Text>
      
      <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        {breakdown.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>No vehicles currently in workshop</Text>
          </View>
        ) : (
          breakdown.map((item, index) => {
            const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0';
            
            return (
              <View key={item.makeModel} style={styles.row}>
                <View style={styles.rankBadge}>
                  <Text style={styles.rankText}>#{index + 1}</Text>
                </View>
                
                <View style={styles.infoContainer}>
                  <Text style={styles.makeModel}>{item.makeModel}</Text>
                  <Text style={styles.plates}>
                    {item.vehicles.slice(0, 3).join(', ')}
                    {item.vehicles.length > 3 ? ` +${item.vehicles.length - 3} more` : ''}
                  </Text>
                </View>
                
                <View style={styles.statsContainer}>
                  <Text style={styles.count}>{item.count}</Text>
                  <Text style={styles.percentage}>{percentage}%</Text>
                </View>
                
                <View style={styles.barContainer}>
                  <View 
                    style={[
                      styles.bar, 
                      { 
                        width: `${percentage}%` as any,
                        backgroundColor: getColorForIndex(index),
                      }
                    ]} 
                  />
                </View>
              </View>
            );
          })
        )}
      </ScrollView>
    </View>
  );
};

const getColorForIndex = (index: number): string => {
  const colors = [
    '#ef4444', // Red
    '#f59e0b', // Orange
    '#10b981', // Green
    '#3b82f6', // Blue
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#14b8a6', // Teal
    '#f97316', // Deep Orange
  ];
  return colors[index % colors.length];
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    padding: 20,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    marginBottom: 20,
  },
  scrollContainer: {
    maxHeight: 500,
  },
  emptyState: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  rankBadge: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: theme.colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  rankText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: theme.colors.textSecondary,
  },
  infoContainer: {
    flex: 1,
    marginRight: 12,
  },
  makeModel: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  plates: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  statsContainer: {
    alignItems: 'flex-end',
    marginRight: 12,
    minWidth: 50,
  },
  count: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  percentage: {
    fontSize: 11,
    color: theme.colors.textSecondary,
  },
  barContainer: {
    width: 80,
    height: 8,
    backgroundColor: theme.colors.background,
    borderRadius: 4,
    overflow: 'hidden',
  },
  bar: {
    height: '100%',
    borderRadius: 4,
  },
});
